import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as logistaService from '@/services/logistaService';

const categories = [
  { value: 'gastronomia',    label: 'Gastronomia',      icon: '🍽️' },
  { value: 'comercio',       label: 'Comércio',          icon: '🛍️' },
  { value: 'cultura',        label: 'Cultura',           icon: '🎭' },
  { value: 'saude',          label: 'Saúde & Bem-estar', icon: '💚' },
  { value: 'servicos',       label: 'Serviços',          icon: '⚙️' },
  { value: 'turismo',        label: 'Turismo',           icon: '🗺️' },
  { value: 'entretenimento', label: 'Entretenimento',    icon: '🎉' },
];

const benefits = [
  { icon: '📍', text: 'Visibilidade no mapa do jogo' },
  { icon: '🎁', text: 'Cria ofertas exclusivas para jogadores' },
  { icon: '📊', text: 'Analytics de visitas e resgates' },
];

const inputClass =
  'w-full rounded-xl border border-white/15 bg-surface/10 px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-accent focus:bg-surface/15 focus:ring-2 focus:ring-accent/20';
const labelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-widest text-white/50';
const sectionLabel = 'mb-3 text-xs font-bold uppercase tracking-widest text-white/30';

export default function LogistaRegisterPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    storeName: '',
    category: 'gastronomia',
    address: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password || !form.storeName) {
      setError('Email, password e nome da loja são obrigatórios.');
      return;
    }
    if (form.password.length < 6) {
      setError('A password deve ter pelo menos 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      await logistaService.register(form);
      setSuccess(true);
      setTimeout(() => navigate('/logista/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível criar a conta.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="grid min-h-full place-items-center bg-[#0f1c2e] px-4 py-8">
        <div className="w-full max-w-sm text-center">
          <div className="mb-4 text-6xl">🎉</div>
          <h2 className="font-serif text-2xl font-black text-white">Conta criada!</h2>
          <p className="mt-2 text-sm text-white/60">
            O teu trial de 14 dias começa agora.<br />A redirecionar para o login...
          </p>
          <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-surface/10">
            <div className="h-full animate-[shrink_2.5s_linear_forwards] rounded-full bg-accent" />
          </div>
          <Link
            to="/logista/login"
            className="mt-4 inline-block text-sm font-semibold text-accent hover:underline"
          >
            Ir para o login agora →
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full bg-[#0f1c2e]">
      {/* Background glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl gap-12 px-6 py-10 lg:grid-cols-[1fr_440px] lg:items-start lg:gap-20">

        {/* Left — value proposition */}
        <div className="hidden lg:flex lg:flex-col lg:justify-center lg:py-10">
          <img src="/branding/logo-hakkencity.webp" alt="Hakken City" className="h-14 w-auto max-w-[180px] object-contain self-start" />
          <h1 className="mt-8 font-serif text-5xl font-black leading-tight text-white">
            O teu negócio<br />
            <span className="text-accent">no mapa do jogo</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/55">
            Liga a tua loja a milhares de jogadores que exploram a cidade. Cria ofertas, acompanha visitas e cresce com a comunidade Hakken City.
          </p>

          <ul className="mt-10 grid gap-5">
            {benefits.map((b) => (
              <li key={b.text} className="flex items-center gap-4">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-surface/[0.07] text-xl">
                  {b.icon}
                </span>
                <span className="text-sm font-semibold text-white/80">{b.text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 rounded-2xl border border-amber-400/20 bg-amber-400/10 p-5">
            <p className="text-sm font-bold text-amber-300">14 dias grátis · sem cartão agora</p>
            <p className="mt-1 text-xs text-amber-400/70">
              Após o trial, continua por 19,99€/mês. Cancela quando quiseres.
            </p>
          </div>
        </div>

        {/* Right — form */}
        <div className="py-6">
          {/* Mobile header */}
          <div className="mb-6 text-center lg:hidden">
            <img src="/branding/logo-hakkencity.webp" alt="Hakken City" className="mx-auto h-14 w-auto object-contain" />
            <h1 className="mt-4 font-serif text-2xl font-black text-white">Registo de Logista</h1>
            <p className="mt-1 text-sm text-white/50">14 dias grátis · Depois 19,99€/mês</p>
          </div>

          <div className="hidden justify-end lg:flex">
            <p className="text-sm text-white/50">
              Já tens conta?{' '}
              <Link to="/logista/login" className="font-bold text-accent hover:underline">
                Entrar
              </Link>
            </p>
          </div>

          <div className="mt-3 rounded-2xl border border-white/10 bg-surface/[0.06] p-6 shadow-2xl backdrop-blur-sm lg:mt-2">
            <form onSubmit={submit} className="grid gap-5">

              {/* Account */}
              <div>
                <p className={sectionLabel}>Conta</p>
                <div className="grid gap-3">
                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      className={inputClass}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        value={form.password}
                        onChange={(e) => set('password', e.target.value)}
                        className={`${inputClass} pr-14`}
                        placeholder="Mínimo 6 caracteres"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-white/40 transition hover:text-white/80"
                        tabIndex={-1}
                      >
                        {showPassword ? 'Ocultar' : 'Ver'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10" />

              {/* Store */}
              <div>
                <p className={sectionLabel}>Dados da loja</p>
                <div className="grid gap-3">
                  <div>
                    <label className={labelClass}>Nome da loja</label>
                    <input
                      type="text"
                      required
                      value={form.storeName}
                      onChange={(e) => set('storeName', e.target.value)}
                      className={inputClass}
                      placeholder="Ex: Pastelaria Central"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Categoria</label>
                    <select
                      value={form.category}
                      onChange={(e) => set('category', e.target.value)}
                      className={inputClass}
                    >
                      {categories.map((c) => (
                        <option key={c.value} value={c.value} className="bg-[#0f1c2e] text-white">
                          {c.icon}  {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Morada <span className="normal-case font-normal text-white/30">(opcional)</span>
                      </label>
                      <input
                        type="text"
                        value={form.address}
                        onChange={(e) => set('address', e.target.value)}
                        className={inputClass}
                        placeholder="Rua, nº, cidade"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Telefone <span className="normal-case font-normal text-white/30">(opcional)</span>
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set('phone', e.target.value)}
                        className={inputClass}
                        placeholder="+351 9XX XXX XXX"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3">
                  <span className="mt-0.5 text-sm">⚠️</span>
                  <p className="text-sm font-semibold text-red-300">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-accent py-3.5 text-sm font-bold text-white shadow-lg transition hover:bg-accent active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? 'A criar conta...' : 'Criar conta e começar trial grátis →'}
              </button>

              <p className="text-center text-xs text-white/30">
                Ao criar conta, aceitas os{' '}
                <span className="font-semibold text-white/50">Termos de Serviço</span>{' '}
                e a{' '}
                <span className="font-semibold text-white/50">Política de Privacidade</span>.
              </p>
            </form>
          </div>

          <div className="mt-5 space-y-2 text-center">
            <p className="text-sm text-white/50">
              Já tens conta?{' '}
              <Link to="/logista/login" className="font-bold text-accent hover:underline">
                Entrar aqui
              </Link>
            </p>
            <p className="text-sm text-white/30">
              <Link to="/" className="transition hover:text-white/60">← Voltar ao início</Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
